import { useRouter } from "next/router";
import Link from "next/link";
import styles from "./Breadcrumbs.module.css";
import { Constants } from "@/schemas/data";
import Head from "next/head";
import Schemas from "@/schemas";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import { fetchCommonData } from "@/services/CommonService";
import { ChevronRight } from "lucide-react";

interface BreadcrumbsProps {
  classValue?: string;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ classValue }) => {
  const router = useRouter();
  const { asPath } = router;

  // Remove query parameters from the path
  const pathWithoutQuery = asPath.split("?")[0];
  const pathArray = pathWithoutQuery.split("/").filter((path) => path);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [headerData, setHeaderData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchCommonData();
        setHeaderData(response.data?.header);
      } catch (err) {
        setError("Error fetching common data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Close dropdown if clicked outside
    if (typeof window !== "undefined") {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setDropdownVisible(false);
        }
      };

      // Add event listener on component mount
      document.addEventListener("mousedown", handleClickOutside);

      // Cleanup on component unmount
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, []);

  if (loading || !headerData) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const coursesData = headerData.main_nav_links[0].nested_list_links;

  const findBreadcrumbs = (slug: string) => {
    // Define a mapping for specific slugs with custom titles
    const customTitles: { [key: string]: string } = {
      "contact-us-for-admissions-and-other-queries-regenesys": "Contact",
      "student-loans": "Get Student Funding",
      "all-academic-programmes": "All Programmes",
      "leadership-team-and-faculty": "Leadership",
      events: "Events",
    };

    // Check if the slug has a custom title
    if (customTitles[slug]) {
      return [{ title: "Home", href: "/" }, { title: customTitles[slug] }];
    }

    // Loop through the courses data to find a match
    for (const school of coursesData) {
      // Check if the slug matches the school URL
      if (school.url === slug) {
        return [
          { title: "Home", href: "/" },
          { title: "All Academic Programme", href: "/all-academic-programmes" },
          { title: capitalizeText(school.title) },
        ];
      }

      // Check if the slug matches any list URL within the school
      for (const list of school.lists) {
        if (list.url === slug) {
          return [
            { title: "Home", href: "/" },
            {
              title: "All Academic Programme",
              href: "/all-academic-programmes",
            },
            { title: capitalizeText(school.title), href: `/${school.url}` },
            { title: capitalizeText(list.url) },
          ];
        }

        // Check if the slug matches any item URL within the list
        for (const item of list.items) {
          if (item.url === slug) {
            return [
              { title: "Home", href: "/" },
              {
                title: "All Academic Programme",
                href: "/all-academic-programmes",
              },
              { title: capitalizeText(school.title), href: `/${school.url}` },
              { title: capitalizeText(list.url), href: `/${list.url}` },
              { title: capitalizeText(item.label) },
            ];
          }
        }
      }

      // Check if the slug matches any link within the school
      for (const item of school.links) {
        if (item.url === slug) {
          return [
            { title: "Home", href: "/" },
            {
              title: "All Academic Programme",
              href: "/all-academic-programmes",
            },
            { title: capitalizeText(school.title), href: `/${school.url}` },
            { title: capitalizeText(item.label) },
          ];
        }
      }
    }

    if (asPath.includes("vacancies")) {
      return [
        { title: "Home", href: "/" },
        {
          title: "Vacancies",
          href: "/careers",
        },
        { title: capitalizeText(slug) },
      ];
    }
    if (asPath.includes("/events/")) {
      return [
        { title: "Home", href: "/" },
        {
          title: "Events",
          href: "/events",
        },
        { title: capitalizeText(slug) },
      ];
    }

    // Fallback for unmatched slugs
    return [{ title: "Home", href: "/" }, { title: capitalizeText(slug) }];
  };

  const capitalizeText = (text: string) => {
    // Remove slashes and hyphens, then split into words
    const cleanedText = text
      .replace(/[\/-]/g, " ") // Replace slashes and hyphens with spaces
      .toLowerCase(); // Convert the entire text to lowercase

    // Capitalize the first letter of each word
    return cleanedText
      .split(" ") // Split text into an array of words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(" "); // Join the words back together
  };

  const handleToggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const breadcrumbData = findBreadcrumbs(pathArray[pathArray.length - 1]);
  // Get the first and last items

  const firstItem = breadcrumbData?.[0] || { title: "Home", href: "/" };
  const lastItem = breadcrumbData?.[breadcrumbData.length - 1];

  // Get the middle items (those that should be hidden by default)
  const middleItems = breadcrumbData?.slice(1, breadcrumbData.length - 1);

  return (
    <>
      <Head>
        <Schemas type={Constants.breadCrumb} data={breadcrumbData} />
      </Head>
      {/* <div
        className={
          classValue === "boxStyle" ? styles.boxStyle : styles.breadcrumbSpacing
        }
      >
        {breadcrumbData?.map((item, index) => (
          <span key={index} className={styles.link}>
            {item.href ? (
              <Link href={item.href}>{item.title}</Link>
            ) : (
              <span className={styles.lastItem}>{item.title}</span>
            )}
            {index < breadcrumbData?.length - 1 && " /"}
          </span>
        ))}
      </div> */}

      <section
        className={
          classValue === "boxStyle" ? styles.boxStyle : styles.breadcrumbSpacing
        }
      >
        <div className="container">
          <div className={styles.breadCrumbInnerBox}>
            {/* Render the first breadcrumb */}
            <div className={styles.link}>
              <Link title={firstItem?.title} href={firstItem?.href || "#"}>
                {firstItem?.title}
              </Link>
              {asPath.includes("/events") && breadcrumbData?.length >= 2 ? (
                <ChevronRight />
              ) : (
                ""
              )}
            </div>

            {/* For pages with just 3 levels (e.g., Home > Events > [Event Name]), 
                directly render the middle item instead of hiding it */}
            {breadcrumbData?.length === 3 && middleItems?.length === 1 && (
              <div className={styles.link}>
                <Link
                  title={middleItems[0]?.title}
                  href={middleItems[0]?.href || "#"}
                >
                  {middleItems[0]?.title}
                </Link>
                <ChevronRight />
              </div>
            )}

            {/* Show ellipsis if there are multiple middle items (more than 3 levels) */}
            {breadcrumbData?.length > 3 && middleItems?.length > 1 && (
              <span className={styles.ellipsis} onClick={handleToggleDropdown}>
                <ChevronRight />
                <span>
                  <Image
                    src="/assets/moreBreadcrumbs.png"
                    alt="more-breadcrumbs"
                    title="more-breadcrumbs"
                    width={32}
                    height={32}
                  />
                </span>
                <ChevronRight />
              </span>
            )}

            {/* Render dropdown with middle items if dropdown is visible */}
            {dropdownVisible && middleItems?.length > 1 && (
              <div ref={dropdownRef} className={styles.dropdown}>
                {middleItems.map((item, index) => (
                  <div key={index} className={styles.dropdownItem}>
                    <Link title={item?.title} href={item?.href || "#"}>
                      {item.title}
                    </Link>
                  </div>
                ))}
              </div>
            )}

            {/* Render the last breadcrumb */}
            {breadcrumbData?.length > 1 && (
              <span className={styles.link}>{lastItem?.title}</span>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Breadcrumbs;
